import React from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link, useParams, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import dayjs from "dayjs";
import { useStudent } from "./hooks/useStudent";
import { showToast } from "../../../core/utils/alert";
import { useGetStudentByIdQuery } from "./api/studentApi";
import { API_CONFIG } from "../../../core/config";
import { useSaveTransactionMutation, useGetInventoriesQuery } from "../../starter-kit/api/starterKitApi";
import { getApiErrorMessage } from "../../../core/utils/errorHandler";
import { useCenter } from "../centers/hooks/useCenter";
import Select from "react-select";

interface StudentModalsProps {
  StudentId?: string;
  studentData?: any;
  showDeleteModal?: boolean;
  setShowDeleteModal?: (show: boolean) => void;
  showDeactivateModal?: boolean;
  setShowDeactivateModal?: (show: boolean) => void;
  showActivateModal?: boolean;
  setShowActivateModal?: (show: boolean) => void;
  showStarterKitModal?: boolean;
  setShowStarterKitModal?: (show: boolean) => void;
  showTransferModal?: boolean;
  setShowTransferModal?: (show: boolean) => void;
  isDeactivateFromDelete?: boolean;
}

const StudentModals: React.FC<StudentModalsProps> = ({
  StudentId,
  studentData,
  showDeleteModal,
  setShowDeleteModal,
  showDeactivateModal,
  setShowDeactivateModal,
  showActivateModal,
  setShowActivateModal,
  showStarterKitModal,
  setShowStarterKitModal,
  showTransferModal,
  setShowTransferModal,
  isDeactivateFromDelete = false,
}) => {
  const navigate = useNavigate();
  const routes = all_routes;
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Month is zero-based, so we add 1
  const day = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${month}-${day}-${year}`;
  const defaultValue = dayjs(formattedDate);

  const { deleteStudent, addNewStudent, deactivateStudent, activateStudent } =
    useStudent();
  const { id } = useParams<{ id: string }>();
  const { data: studentResponse } = useGetStudentByIdQuery(
    { id: id || "" },
    { skip: !id }
  );
  const student = studentResponse?.data?.student;

  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse } = useCenterList({ skip: !showTransferModal });
  const centers = centerResponse?.data?.items || [];
  const currentCenterId = studentData?.center?.id || studentData?.center_id;
  const centerOptions = centers
    .filter((center: any) => center.id !== currentCenterId)
    .map((center: any) => ({
      value: center.id.toString(),
      label: center.name,
    }));

  const [assignStarterKit, { isLoading: isAssigning }] = useSaveTransactionMutation();
  const [selectedCenter, setSelectedCenter] = React.useState<any>(null);
  const [remarks, setRemarks] = React.useState("");
  const [isTransferring, setIsTransferring] = React.useState(false);
  const { transferStudent } = useStudent();

  const courseId = Number(studentData?.course?.id || studentData?.course_id || student?.course?.id || student?.course_id);
  const centerId = studentData?.center?.id || studentData?.center_id || student?.center?.id;
  const courseName = studentData?.course?.name || student?.course?.name || "Unknown Course";

  const { data: inventoryData, isLoading: isInventoryLoading } = useGetInventoriesQuery(
    { center_id: centerId, course_id: courseId, is_main_center_stock: 0 },
    { skip: !centerId || !courseId || !showStarterKitModal }
  );

  const centerInventories = inventoryData?.data?.items || [];
  const centerInventory = centerInventories.find((i: any) => Number(i.course_id) === courseId || Number(i.course?.id) === courseId);
  const hasAvailableStock = (centerInventory?.available_stock || 0) > 0;

  const handleAssignStarterKit = async () => {
    if (StudentId) {
      try {
        const result = await assignStarterKit({
          type: "assigned_to_student",
          quantity: 1,
          course_id: Number(studentData?.course?.id || studentData?.course_id || student?.course?.id || student?.course_id),
          from_center_id: studentData?.center?.id || studentData?.center_id,
          student_id: Number(StudentId),
        }).unwrap();

        if (result.success) {
          showToast({
            title: "Success",
            text: "Starter kit assigned successfully",
            icon: "success",
          });
          setShowStarterKitModal?.(false);
        }
      } catch (error: any) {
        showToast({
          title: "Error",
          text: getApiErrorMessage(error) || "Failed to assign starter kit",
          icon: "error",
        });
      }
    }
  };

  const handleTransfer = async () => {
    if (StudentId && selectedCenter) {
      setIsTransferring(true);
      const result: any = await transferStudent({
        student_id: Number(StudentId),
        to_center_id: Number(selectedCenter.value),
        remarks: remarks,
      });

      if (result.success) {
        showToast({
          title: "Success",
          text: "Student transferred successfully",
          icon: "success",
        });
        setShowTransferModal?.(false);
        setSelectedCenter(null);
        setRemarks("");
      } else {
        showToast({
          title: "Error",
          text: result.error || "Failed to transfer student",
          icon: "error",
        });
      }
      setIsTransferring(false);
    } else {
      showToast({
        title: "Error",
        text: "Please select a center to transfer to",
        icon: "error",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // The student is already created, we just want to skip document upload and go to list
    navigate(routes.studentList);
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    // The student is already created, navigate to their document upload page
    if (studentData?.id || StudentId) {
      navigate(
        routes.studentDocuments.replace(
          ":id",
          (studentData?.id || StudentId).toString()
        )
      );
    } else {
      navigate(routes.studentList);
    }
  };

  const handleDelete = async () => {
    if (StudentId) {
      const result: any = await deleteStudent(StudentId);

      if (result.success) {
        setShowDeleteModal?.(false);
        showToast({
          title: "Success",
          text: result.message || "Record deleted successfully",
          icon: "success",
        });
      } else if (result.status === 409) {
        setShowDeleteModal?.(false);
        setShowDeactivateModal?.(true);
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not delete the record",
          icon: "error",
        });
      }
    }
  };

  const handleActivate = async () => {
    if (StudentId) {
      const result: any = await activateStudent(StudentId);

      if (result.success) {
        showToast({
          title: "Success",
          text: "Student activated successfully",
          icon: "success",
        });
        setShowActivateModal?.(false);
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not activate the student",
          icon: "error",
        });
      }
    }
  };
  const handleDeactivate = async () => {
    if (StudentId) {
      const result: any = await deactivateStudent(StudentId);

      if (result.success) {
        showToast({
          title: "Success",
          text: "Student deactivated successfully",
          icon: "success",
        });
        setShowDeactivateModal?.(false);
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not deactivate the student",
          icon: "error",
        });
      }
    }
  };
  return (
    <>
      {/* Delete Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          id="delete-modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleDelete();
                }}
              >
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    You want to delete the record, this can't be undone once you
                    delete.
                  </p>
                  <div className="d-flex justify-content-center">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={() => setShowDeleteModal?.(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-danger">
                      Yes, Delete
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Deactivation Modal */}
      {showDeactivateModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          id="deactivate-modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleDeactivate();
                }}
              >
                <div className="modal-body text-center p-4">
                  <div className="warning-icon mb-4">
                    <i
                      className="ti ti-alert-triangle"
                      style={{ fontSize: "4rem", color: "#ffc107" }}
                    />
                  </div>
                  <h4 className="mb-3 fw-semibold">
                    {isDeactivateFromDelete
                      ? "Associated Records Found"
                      : "Confirm Deactivation"}
                  </h4>
                  <p className="text-muted mb-4">
                    {isDeactivateFromDelete
                      ? "This student has associated records that prevent deletion. Would you like to deactivate the student instead?"
                      : "Are you sure you want to deactivate this student? The student's data will be preserved but they won't be able to access the system."}
                  </p>
                  <div className="d-flex justify-content-center gap-3">
                    <button
                      type="button"
                      className="btn btn-light px-4"
                      onClick={() => {
                        setShowDeactivateModal?.(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-warning px-4">
                      Yes, Deactivate
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Activation Modal */}
      {showActivateModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          id="activate-modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleActivate();
                }}
              >
                <div className="modal-body text-center p-4">
                  <div className="success-icon mb-4">
                    <i
                      className="ti ti-user-check"
                      style={{ fontSize: "4rem", color: "#198754" }}
                    />
                  </div>
                  <h4 className="mb-3 fw-semibold">Confirm Activation</h4>
                  <p className="text-muted mb-4">
                    Are you sure you want to activate this student? The student
                    will regain access to the system.
                  </p>
                  <div className="d-flex justify-content-center gap-3">
                    <button
                      type="button"
                      className="btn btn-light px-4"
                      onClick={() => setShowActivateModal?.(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-success px-4">
                      Yes, Activate
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Starter Kit Modal */}
      {showStarterKitModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          id="starter-kit-modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAssignStarterKit();
                }}
              >
                <div className="modal-body text-center p-4">
                  <div className="success-icon mb-4">
                    <i
                      className="ti ti-package"
                      style={{ fontSize: "4rem", color: "#0d6efd" }}
                    />
                  </div>
                  <h4 className="mb-3 fw-semibold">Assign Starter Kit</h4>
                  {isInventoryLoading ? (
                    <p className="text-muted mb-4">Checking inventory status...</p>
                  ) : hasAvailableStock ? (
                    <p className="text-muted mb-4">
                      Are you sure you want to assign a <strong>{courseName}</strong> starter kit to this student?
                      This will deduct 1 kit from your center's inventory.
                    </p>
                  ) : (
                    <div className="alert alert-danger mb-4">
                      Starter kit stock is not available for <strong>{courseName}</strong> at this center.
                      Please ensure stock availablity before assigning.
                    </div>
                  )}
                  <div className="d-flex justify-content-center gap-3">
                    <button
                      type="button"
                      className="btn btn-light px-4"
                      onClick={() => setShowStarterKitModal?.(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary px-4"
                      disabled={isAssigning || !hasAvailableStock || isInventoryLoading}
                    >
                      {isAssigning ? "Assigning..." : "Yes, Assign"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Student Modal */}
      {showTransferModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          id="transfer-student-modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Transfer Student</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={() => setShowTransferModal?.(false)}
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleTransfer();
                }}
              >
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label">
                      Transfer To Center <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={centerOptions}
                      value={selectedCenter}
                      onChange={setSelectedCenter}
                      placeholder="Select Center"
                      className="select-custom"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Remarks</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Enter reason for transfer"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                    ></textarea>
                  </div>
                  <div className="d-flex justify-content-end gap-3 mt-4">
                    <button
                      type="button"
                      className="btn btn-light px-4"
                      onClick={() => setShowTransferModal?.(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary px-4"
                      disabled={isTransferring || !selectedCenter}
                    >
                      {isTransferring ? "Transferring..." : "Transfer"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add modal backdrop when any modal is shown */}
      {(showDeleteModal || showDeactivateModal || showActivateModal || showStarterKitModal || showTransferModal) && (
        <div className="modal-backdrop fade show"></div>
      )}

      {/* Login Details */}
      <div className="modal fade" id="login_detail">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Login Details</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="student-detail-info">
                <span className="student-img">
                  {student?.docs?.photo ? (
                    <img
                      src={`${API_CONFIG.BASE_URL}students/get-thumbnail/${student.docs.photo
                        }?v=${new Date().getTime()}`}
                      alt="Student"
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          student.profile.gender?.toLowerCase() === "female"
                            ? "/assets/img/profiles/default-female.png"
                            : "/assets/img/profiles/default-male.png";
                      }}
                    />
                  ) : (
                    <ImageWithBasePath
                      src={
                        student?.profile?.gender?.toLowerCase() === "female"
                          ? "assets/img/profiles/default-female.png"
                          : "assets/img/profiles/default-male.png"
                      }
                      alt="Student"
                    />
                  )}
                </span>
                <div className="name-info">
                  <h6>
                    {student?.name}{" "}
                    <span>{student?.course?.name}</span>
                  </h6>
                </div>
              </div>
              <div className="table-responsive custom-table no-datatable_length">
                <table className="table datanew">
                  <thead className="thead-light">
                    <tr>
                      <th>User Type</th>
                      <th>User Name</th>
                      <th>Password</th>
                    </tr>
                  </thead>
                  <tbody>
                    {student?.user && (
                      <tr>
                        <td>Student</td>
                        <td>{student.user.username || "N/A"}</td>
                        <td>{student.user.password || "N/A"}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <Link
                to="#"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
              >
                Close
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* /Login Details */}

      {/* Document Upload Modal */}
      <div className="modal fade" id="upload_modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form>
              <div className="modal-body text-center">
                <span className="upload-icon">
                  <i className="ti ti-file-upload" style={{ fontSize: "40px", color: "#3D5EE1" }} />
                </span>
                <h4 className="mt-3">Upload Documents</h4>
                <p>Would you like to upload documents for this student now?</p>
                <div className="d-flex justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    onClick={handleSubmit}
                    data-bs-dismiss="modal"
                  >
                    Skip for Now
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleContinue}
                    data-bs-dismiss="modal"
                  >
                    Yes, Upload
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentModals;
